import {
  Button,
  FormInput,
  FormPrice,
  FormRoot,
  FormSelect,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { SparklesIcon, CalendarDaysIcon, UserCogIcon } from "lucide-react";
import { z } from "zod";
import type {
  CreateInstructorDTO,
  InstructorPayoutBasis,
  InstructorPayoutRateItemDTO,
  UpdateInstructorDTO,
} from "@/modules/master/domain/contracts";
import {
  defaultMonthlyFreeGeneralClassSessions,
  type PrivatePackagePayoutMode,
} from "@/modules/master/domain/contracts/instructors";
import { cn } from "@devhop/ui";
import { isDevMode } from "@/shared/lib/config";
import { getRandomMockData, mockInstructors } from "../dev-mock-data";

const basisField = z.enum(["per_hour", "per_session"]);
const amountField = z.coerce.number().int().min(0).default(0);

const InstructorFormSchema = z.object({
  name: z.string().min(1, "ຊື່ຕ້ອງບໍ່ວ່າງ"),
  phoneNumber: z.string().nullable().optional(),
  email: z.string().email("ຮູບແບບອີເມວບໍ່ຖືກຕ້ອງ").nullable().optional(),
  bankAccountNumber: z.string().nullable().optional(),
  employmentType: z.enum(["full_time", "freelance"]),
  monthlyFreeGeneralClassSessions: z.coerce.number().int().min(0).max(999),
  privatePackagePayoutMode: z.enum(["package_tier", "instructor_rate"]),
  generalClassStudioPayoutBasis: basisField,
  generalClassStudioAmountKip: amountField,
  generalClassOffSitePayoutBasis: basisField,
  generalClassOffSiteAmountKip: amountField,
  privateLessonStudioPayoutBasis: basisField,
  privateLessonStudioAmountKip: amountField,
  privateLessonOffSiteNearPayoutBasis: basisField,
  privateLessonOffSiteNearAmountKip: amountField,
  privateLessonOffSiteFarPayoutBasis: basisField,
  privateLessonOffSiteFarAmountKip: amountField,
  partnerPayoutBasis: basisField,
  partnerAmountKip: amountField,
  roomRentalAssistPayoutBasis: basisField,
  roomRentalAssistAmountKip: amountField,
  generalClassAssistStudioPayoutBasis: basisField,
  generalClassAssistStudioAmountKip: amountField,
  generalClassAssistOffSitePayoutBasis: basisField,
  generalClassAssistOffSiteAmountKip: amountField,
  status: z.enum(["active", "inactive"]).default("active"),
  notes: z.string().nullable().optional(),
});

export type InstructorFormValues = z.infer<typeof InstructorFormSchema>;

function buildPayoutRates(
  v: InstructorFormValues,
): InstructorPayoutRateItemDTO[] {
  return [
    {
      workKind: "general_class",
      locationKind: "studio",
      payoutBasis: v.generalClassStudioPayoutBasis,
      amountKip: v.generalClassStudioAmountKip,
    },
    {
      workKind: "general_class",
      locationKind: "off_site",
      payoutBasis: v.generalClassOffSitePayoutBasis,
      amountKip: v.generalClassOffSiteAmountKip,
    },
    {
      workKind: "private_lesson",
      locationKind: "studio",
      payoutBasis: v.privateLessonStudioPayoutBasis,
      amountKip: v.privateLessonStudioAmountKip,
    },
    {
      workKind: "private_lesson",
      locationKind: "off_site_near",
      payoutBasis: v.privateLessonOffSiteNearPayoutBasis,
      amountKip: v.privateLessonOffSiteNearAmountKip,
    },
    {
      workKind: "private_lesson",
      locationKind: "off_site_far",
      payoutBasis: v.privateLessonOffSiteFarPayoutBasis,
      amountKip: v.privateLessonOffSiteFarAmountKip,
    },
    {
      workKind: "partner",
      locationKind: "studio",
      payoutBasis: v.partnerPayoutBasis,
      amountKip: v.partnerAmountKip,
    },
    {
      workKind: "room_rental_assist",
      locationKind: "studio",
      payoutBasis: v.roomRentalAssistPayoutBasis,
      amountKip: v.roomRentalAssistAmountKip,
    },
    {
      workKind: "general_class_assist",
      locationKind: "studio",
      payoutBasis: v.generalClassAssistStudioPayoutBasis,
      amountKip: v.generalClassAssistStudioAmountKip,
    },
    {
      workKind: "general_class_assist",
      locationKind: "off_site",
      payoutBasis: v.generalClassAssistOffSitePayoutBasis,
      amountKip: v.generalClassAssistOffSiteAmountKip,
    },
  ];
}

export function instructorFormValuesToCreateDTO(
  v: InstructorFormValues,
): CreateInstructorDTO {
  const {
    generalClassStudioPayoutBasis: _a,
    generalClassStudioAmountKip: _b,
    generalClassOffSitePayoutBasis: _c,
    generalClassOffSiteAmountKip: _d,
    privateLessonStudioPayoutBasis: _e,
    privateLessonStudioAmountKip: _f,
    privateLessonOffSiteNearPayoutBasis: _g,
    privateLessonOffSiteNearAmountKip: _h,
    privateLessonOffSiteFarPayoutBasis: _i,
    privateLessonOffSiteFarAmountKip: _j,
    partnerPayoutBasis: _k,
    partnerAmountKip: _l,
    roomRentalAssistPayoutBasis: _m,
    roomRentalAssistAmountKip: _n,
    generalClassAssistStudioPayoutBasis: _o,
    generalClassAssistStudioAmountKip: _p,
    generalClassAssistOffSitePayoutBasis: _q,
    generalClassAssistOffSiteAmountKip: _r,
    ...rest
  } = v;
  return {
    name: rest.name,
    phoneNumber: rest.phoneNumber || null,
    email: rest.email || null,
    bankAccountNumber: rest.bankAccountNumber?.trim() || null,
    employmentType: rest.employmentType,
    monthlyFreeGeneralClassSessions: rest.monthlyFreeGeneralClassSessions,
    privatePackagePayoutMode: rest.privatePackagePayoutMode,
    status: rest.status,
    notes: rest.notes || null,
    payoutRates: buildPayoutRates(v),
  };
}

export function instructorFormValuesToUpdateDTO(
  v: InstructorFormValues,
): UpdateInstructorDTO {
  const {
    generalClassStudioPayoutBasis: _a,
    generalClassStudioAmountKip: _b,
    generalClassOffSitePayoutBasis: _c,
    generalClassOffSiteAmountKip: _d,
    privateLessonStudioPayoutBasis: _e,
    privateLessonStudioAmountKip: _f,
    privateLessonOffSiteNearPayoutBasis: _g,
    privateLessonOffSiteNearAmountKip: _h,
    privateLessonOffSiteFarPayoutBasis: _i,
    privateLessonOffSiteFarAmountKip: _j,
    partnerPayoutBasis: _k,
    partnerAmountKip: _l,
    roomRentalAssistPayoutBasis: _m,
    roomRentalAssistAmountKip: _n,
    generalClassAssistStudioPayoutBasis: _o,
    generalClassAssistStudioAmountKip: _p,
    generalClassAssistOffSitePayoutBasis: _q,
    generalClassAssistOffSiteAmountKip: _r,
    ...rest
  } = v;
  return {
    name: rest.name,
    phoneNumber: rest.phoneNumber || null,
    email: rest.email || null,
    bankAccountNumber: rest.bankAccountNumber?.trim() || null,
    employmentType: rest.employmentType,
    monthlyFreeGeneralClassSessions: rest.monthlyFreeGeneralClassSessions,
    privatePackagePayoutMode: rest.privatePackagePayoutMode,
    status: rest.status,
    notes: rest.notes || null,
    payoutRates: buildPayoutRates(v),
  };
}

function payoutRatesToFormDefaults(
  rates: InstructorPayoutRateItemDTO[] | undefined,
): Pick<
  InstructorFormValues,
  | "generalClassStudioPayoutBasis"
  | "generalClassStudioAmountKip"
  | "generalClassOffSitePayoutBasis"
  | "generalClassOffSiteAmountKip"
  | "privateLessonStudioPayoutBasis"
  | "privateLessonStudioAmountKip"
  | "privateLessonOffSiteNearPayoutBasis"
  | "privateLessonOffSiteNearAmountKip"
  | "privateLessonOffSiteFarPayoutBasis"
  | "privateLessonOffSiteFarAmountKip"
  | "partnerPayoutBasis"
  | "partnerAmountKip"
  | "roomRentalAssistPayoutBasis"
  | "roomRentalAssistAmountKip"
  | "generalClassAssistStudioPayoutBasis"
  | "generalClassAssistStudioAmountKip"
  | "generalClassAssistOffSitePayoutBasis"
  | "generalClassAssistOffSiteAmountKip"
> {
  const m = Object.fromEntries(
    (rates ?? []).map((r) => [
      `${r.workKind}:${r.locationKind ?? "studio"}`,
      { payoutBasis: r.payoutBasis, amountKip: r.amountKip },
    ]),
  ) as Record<
    string,
    { payoutBasis: InstructorPayoutBasis; amountKip: number }
  >;
  const legacyPrivateOffSite = m["private_lesson:off_site"];
  return {
    generalClassStudioPayoutBasis:
      m["general_class:studio"]?.payoutBasis ?? "per_hour",
    generalClassStudioAmountKip: m["general_class:studio"]?.amountKip ?? 0,
    generalClassOffSitePayoutBasis:
      m["general_class:off_site"]?.payoutBasis ?? "per_hour",
    generalClassOffSiteAmountKip: m["general_class:off_site"]?.amountKip ?? 0,
    privateLessonStudioPayoutBasis:
      m["private_lesson:studio"]?.payoutBasis ?? "per_hour",
    privateLessonStudioAmountKip: m["private_lesson:studio"]?.amountKip ?? 0,
    privateLessonOffSiteNearPayoutBasis:
      m["private_lesson:off_site_near"]?.payoutBasis ??
      legacyPrivateOffSite?.payoutBasis ??
      "per_hour",
    privateLessonOffSiteNearAmountKip:
      m["private_lesson:off_site_near"]?.amountKip ??
      legacyPrivateOffSite?.amountKip ??
      0,
    privateLessonOffSiteFarPayoutBasis:
      m["private_lesson:off_site_far"]?.payoutBasis ?? "per_hour",
    privateLessonOffSiteFarAmountKip:
      m["private_lesson:off_site_far"]?.amountKip ?? 0,
    partnerPayoutBasis: m["partner:studio"]?.payoutBasis ?? "per_hour",
    partnerAmountKip: m["partner:studio"]?.amountKip ?? 0,
    roomRentalAssistPayoutBasis:
      m["room_rental_assist:studio"]?.payoutBasis ?? "per_session",
    roomRentalAssistAmountKip:
      m["room_rental_assist:studio"]?.amountKip ?? 0,
    generalClassAssistStudioPayoutBasis:
      m["general_class_assist:studio"]?.payoutBasis ?? "per_session",
    generalClassAssistStudioAmountKip:
      m["general_class_assist:studio"]?.amountKip ?? 0,
    generalClassAssistOffSitePayoutBasis:
      m["general_class_assist:off_site"]?.payoutBasis ?? "per_session",
    generalClassAssistOffSiteAmountKip:
      m["general_class_assist:off_site"]?.amountKip ?? 0,
  };
}

const BASIS_OPTIONS: {
  value: InstructorPayoutBasis;
  label: string;
}[] = [
  { value: "per_hour", label: "ຕໍ່ຊົ່ວໂມງ" },
  { value: "per_session", label: "ຕໍ່ຄັ້ງ" },
];

const PRIVATE_PACKAGE_PAYOUT_OPTIONS: {
  value: PrivatePackagePayoutMode;
  label: string;
}[] = [
  {
    value: "package_tier",
    label: "ເຣດຕາຍຕົວຕາມຈຳນວນຄົນ (1–2: 150,000 · 3–4: 300,000 · 5–10: 350,000)",
  },
  {
    value: "instructor_rate",
    label: "ເຣດສ່ວນຕົວຂອງຄູຄົນນີ້",
  },
];

function PayoutBasisField({
  name,
  label,
}: {
  name:
    | "generalClassStudioPayoutBasis"
    | "generalClassOffSitePayoutBasis"
    | "privateLessonStudioPayoutBasis"
    | "privateLessonOffSiteNearPayoutBasis"
    | "privateLessonOffSiteFarPayoutBasis"
    | "partnerPayoutBasis"
    | "roomRentalAssistPayoutBasis"
    | "generalClassAssistStudioPayoutBasis"
    | "generalClassAssistOffSitePayoutBasis";
  label: string;
}) {
  return <FormSelect name={name} label={label} options={BASIS_OPTIONS} />;
}

function LocationPayoutFields({
  basisName,
  amountName,
  basisLabel,
}: {
  basisName:
    | "generalClassStudioPayoutBasis"
    | "generalClassOffSitePayoutBasis"
    | "privateLessonStudioPayoutBasis"
    | "privateLessonOffSiteNearPayoutBasis"
    | "privateLessonOffSiteFarPayoutBasis"
    | "generalClassAssistStudioPayoutBasis"
    | "generalClassAssistOffSitePayoutBasis";
  amountName:
    | "generalClassStudioAmountKip"
    | "generalClassOffSiteAmountKip"
    | "privateLessonStudioAmountKip"
    | "privateLessonOffSiteNearAmountKip"
    | "privateLessonOffSiteFarAmountKip"
    | "generalClassAssistStudioAmountKip"
    | "generalClassAssistOffSiteAmountKip";
  basisLabel: string;
}) {
  return (
    <div className="space-y-3">
      <PayoutBasisField name={basisName} label={basisLabel} />
      <FormPrice
        name={amountName}
        label="ຈຳນວນ (₭)"
        placeholder="0"
        currencySymbol="₭"
      />
    </div>
  );
}

export function InstructorForm({
  initialValues,
  onSubmit,
  submitting,
}: {
  initialValues?: Partial<
    InstructorFormValues & {
      payoutRates?: InstructorPayoutRateItemDTO[];
      isGuest?: boolean;
    }
  >;
  onSubmit: (values: InstructorFormValues) => void;
  submitting?: boolean;
}) {
  const rateDefaults = payoutRatesToFormDefaults(initialValues?.payoutRates);
  const isGuest = initialValues?.isGuest === true;
  const defaultEmploymentType = initialValues?.employmentType || "full_time";

  const methods = RHF.useForm<InstructorFormValues>({
    resolver: zodResolver(InstructorFormSchema),
    defaultValues: {
      name: initialValues?.name || "",
      phoneNumber: initialValues?.phoneNumber || "",
      email: initialValues?.email || "",
      bankAccountNumber: initialValues?.bankAccountNumber || "",
      employmentType: defaultEmploymentType,
      monthlyFreeGeneralClassSessions:
        initialValues?.monthlyFreeGeneralClassSessions ??
        defaultMonthlyFreeGeneralClassSessions(defaultEmploymentType),
      privatePackagePayoutMode:
        initialValues?.privatePackagePayoutMode ?? "package_tier",
      generalClassStudioPayoutBasis: rateDefaults.generalClassStudioPayoutBasis,
      generalClassStudioAmountKip: rateDefaults.generalClassStudioAmountKip,
      generalClassOffSitePayoutBasis:
        rateDefaults.generalClassOffSitePayoutBasis,
      generalClassOffSiteAmountKip: rateDefaults.generalClassOffSiteAmountKip,
      privateLessonStudioPayoutBasis: rateDefaults.privateLessonStudioPayoutBasis,
      privateLessonStudioAmountKip: rateDefaults.privateLessonStudioAmountKip,
      privateLessonOffSiteNearPayoutBasis:
        rateDefaults.privateLessonOffSiteNearPayoutBasis,
      privateLessonOffSiteNearAmountKip:
        rateDefaults.privateLessonOffSiteNearAmountKip,
      privateLessonOffSiteFarPayoutBasis:
        rateDefaults.privateLessonOffSiteFarPayoutBasis,
      privateLessonOffSiteFarAmountKip:
        rateDefaults.privateLessonOffSiteFarAmountKip,
      partnerPayoutBasis: rateDefaults.partnerPayoutBasis,
      partnerAmountKip: rateDefaults.partnerAmountKip,
      roomRentalAssistPayoutBasis: rateDefaults.roomRentalAssistPayoutBasis,
      roomRentalAssistAmountKip: rateDefaults.roomRentalAssistAmountKip,
      generalClassAssistStudioPayoutBasis:
        rateDefaults.generalClassAssistStudioPayoutBasis,
      generalClassAssistStudioAmountKip:
        rateDefaults.generalClassAssistStudioAmountKip,
      generalClassAssistOffSitePayoutBasis:
        rateDefaults.generalClassAssistOffSitePayoutBasis,
      generalClassAssistOffSiteAmountKip:
        rateDefaults.generalClassAssistOffSiteAmountKip,
      status: initialValues?.status || "active",
      notes: initialValues?.notes || "",
    },
  });

  const handleAutoFill = () => {
    const mockData = getRandomMockData(mockInstructors);
    const pr = payoutRatesToFormDefaults(mockData.payoutRates);
    methods.reset({
      name: mockData.name,
      phoneNumber: mockData.phoneNumber ?? "",
      email: mockData.email ?? "",
      bankAccountNumber: "",
      employmentType: mockData.employmentType,
      monthlyFreeGeneralClassSessions: defaultMonthlyFreeGeneralClassSessions(
        mockData.employmentType,
      ),
      privatePackagePayoutMode: "package_tier",
      ...pr,
      status: mockData.status,
      notes: mockData.notes ?? "",
    });
  };

  return (
    <FormRoot methods={methods} onSubmit={onSubmit} className="space-y-6">
      <div className="space-y-6">
        <section className="rounded-xl border bg-card p-5 shadow-sm">
          <h3 className="mb-4 font-semibold text-sm">ຂໍ້ມູນພື້ນຖານ</h3>
          <div className="space-y-4">
            <FormInput name="name" label="ຊື່" placeholder="ປ້ອນຊື່ຄູສອນ" required />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormInput
                name="phoneNumber"
                label="ເບີໂທ"
                placeholder="ປ້ອນເບີໂທ (ບໍ່ບັງຄັບ)"
              />

              <FormInput
                name="email"
                label="ອີເມວ"
                type="email"
                placeholder="ປ້ອນອີເມວ (ບໍ່ບັງຄັບ)"
              />
            </div>

            <FormInput
              name="bankAccountNumber"
              label="ເລກບັນຊີທະນາຄານ"
              placeholder="ປ້ອນເລກບັນຊີສຳລັບໂອນຄ່າຈ້າງ (ບໍ່ບັງຄັບ)"
            />
          </div>
        </section>

        <section className="rounded-xl border bg-card p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <UserCogIcon className="size-4 text-muted-foreground" />
            <h3 className="font-semibold text-sm">ປະເພດການຈ້າງ & Retainer</h3>
          </div>

          <RHF.Controller
            name="employmentType"
            control={methods.control}
            render={({ field }) => (
              <div className="space-y-2">
                <label htmlFor="employmentType" className="font-medium text-sm">
                  ປະເພດການຈ້າງ
                </label>
                <div className="inline-flex rounded-lg border bg-muted/30 p-1">
                  <Button
                    type="button"
                    size="sm"
                    variant={field.value === "full_time" ? "default" : "ghost"}
                    className={cn(
                      "min-w-[88px] rounded-md",
                      field.value === "full_time" && "shadow-sm",
                    )}
                    onClick={() => {
                      field.onChange("full_time");
                      methods.setValue(
                        "monthlyFreeGeneralClassSessions",
                        defaultMonthlyFreeGeneralClassSessions("full_time"),
                      );
                    }}
                  >
                    ປະຈຳ
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={field.value === "freelance" ? "default" : "ghost"}
                    className={cn(
                      "min-w-[88px] rounded-md",
                      field.value === "freelance" && "shadow-sm",
                    )}
                    onClick={() => {
                      field.onChange("freelance");
                      methods.setValue(
                        "monthlyFreeGeneralClassSessions",
                        defaultMonthlyFreeGeneralClassSessions("freelance"),
                      );
                    }}
                  >
                    ຟຣີແລນ
                  </Button>
                </div>
              </div>
            )}
          />

          {!isGuest ? (
            <div className="mt-4 rounded-lg border border-emerald-500/25 bg-emerald-500/5 p-4">
              <div className="mb-3 flex items-start gap-3">
                <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
                  <CalendarDaysIcon className="size-4" aria-hidden />
                </div>
                <div>
                  <p className="font-medium text-sm">
                    Session ຄລາສທົ່ວໄປຟຣີຕໍ່ເດືອນ
                  </p>
                  <p className="text-muted-foreground text-xs">
                    Lead general class — N ຄັ້ງກ່ອນຄິດເງິນເພີ່ມ
                  </p>
                </div>
              </div>
              <div className="mb-3 flex flex-wrap gap-2">
                {[
                  { value: 0, label: "ບໍ່ມີ (0)" },
                  { value: 8, label: "ມາດຕະຖານ (8)" },
                ].map((preset) => (
                  <Button
                    key={preset.value}
                    type="button"
                    size="sm"
                    variant={
                      methods.watch("monthlyFreeGeneralClassSessions") ===
                      preset.value
                        ? "default"
                        : "outline"
                    }
                    className={cn(
                      methods.watch("monthlyFreeGeneralClassSessions") ===
                        preset.value &&
                        preset.value === 8 &&
                        "border-emerald-600 bg-emerald-600 hover:bg-emerald-600",
                    )}
                    onClick={() =>
                      methods.setValue(
                        "monthlyFreeGeneralClassSessions",
                        preset.value,
                      )
                    }
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
              <FormInput
                name="monthlyFreeGeneralClassSessions"
                label="ຈຳນວນ session ຟຣີ"
                type="number"
                min={0}
                max={999}
              />
            </div>
          ) : null}
        </section>

        <section className="space-y-4 rounded-xl border bg-card p-5 shadow-sm">
          <div>
            <h3 className="font-semibold text-sm">
              ອັດຕາ / ຈຳນວນເງິນ (ກີບ)
            </h3>
            <p className="text-muted-foreground text-xs">
              ແຍກຕາມປະເພດງານ ແລະ ສະຖານທີ່
            </p>
          </div>

          <div className="space-y-3 rounded-md border p-4">
            <p className="font-medium text-sm">ຄລາສທົ່ວໄປ</p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-1 rounded-md border bg-muted/30 p-3">
                <p className="font-medium text-muted-foreground text-xs">
                  ໃນສະຖານທີ່
                </p>
                <LocationPayoutFields
                  basisName="generalClassStudioPayoutBasis"
                  amountName="generalClassStudioAmountKip"
                  basisLabel="ວິທີຄິດ"
                />
              </div>
              <div className="space-y-1 rounded-md border bg-muted/30 p-3">
                <p className="font-medium text-muted-foreground text-xs">
                  ນອກສະຖານທີ່
                </p>
                <LocationPayoutFields
                  basisName="generalClassOffSitePayoutBasis"
                  amountName="generalClassOffSiteAmountKip"
                  basisLabel="ວິທີຄິດ"
                />
              </div>
            </div>
          </div>

          <div className="space-y-3 rounded-md border p-4">
            <p className="font-medium text-sm">ສ່ວນຕົວ</p>
            <FormSelect
              name="privatePackagePayoutMode"
              label="Private Package ທີ່ສະຕູດິໂອ"
              options={PRIVATE_PACKAGE_PAYOUT_OPTIONS}
            />
            <p className="text-muted-foreground text-xs">
              ເຣດຕາຍຕົວໃຊ້ກັບ Private Package ໃນສະຕູດິໂອເທົ່ານັ້ນ.
              ເລືອກເຣດສ່ວນຕົວຂອງຄູ ແລ້ວຄັ້ງນັ້ນຈະຈ່າຍຕາມອັດຕາໃນສະຖານທີ່ຂອງຄູຄົນນີ້.
              ນອກສະຖານທີ່ໃຊ້ເຣດສ່ວນຕົວຢູ່ແລ້ວ.
            </p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="space-y-1 rounded-md border bg-muted/30 p-3">
                <p className="font-medium text-muted-foreground text-xs">
                  ໃນສະຖານທີ່
                </p>
                <LocationPayoutFields
                  basisName="privateLessonStudioPayoutBasis"
                  amountName="privateLessonStudioAmountKip"
                  basisLabel="ວິທີຄິດ"
                />
              </div>
              <div className="space-y-1 rounded-md border bg-muted/30 p-3">
                <p className="font-medium text-muted-foreground text-xs">
                  ນອກສະຖານທີ່ໃກ້
                </p>
                <LocationPayoutFields
                  basisName="privateLessonOffSiteNearPayoutBasis"
                  amountName="privateLessonOffSiteNearAmountKip"
                  basisLabel="ວິທີຄິດ"
                />
              </div>
              <div className="space-y-1 rounded-md border bg-muted/30 p-3">
                <p className="font-medium text-muted-foreground text-xs">
                  ນອກສະຖານທີ່ໄກ
                </p>
                <LocationPayoutFields
                  basisName="privateLessonOffSiteFarPayoutBasis"
                  amountName="privateLessonOffSiteFarAmountKip"
                  basisLabel="ວິທີຄິດ"
                />
              </div>
            </div>
          </div>

          <div className="max-w-sm space-y-3 rounded-md border p-4">
            <p className="font-medium text-sm">Partner</p>
            <p className="text-muted-foreground text-xs">
              ບໍ່ແຍກຕາມສະຖານທີ່
            </p>
            <PayoutBasisField name="partnerPayoutBasis" label="ວິທີຄິດ" />
            <FormPrice
              name="partnerAmountKip"
              label="ຈຳນວນ (₭)"
              placeholder="0"
              currencySymbol="₭"
            />
          </div>
          <div className="max-w-sm space-y-3 rounded-md border p-4">
            <p className="font-medium text-sm">ຊ່ວຍສອນເຊົ່າຫ້ອງ</p>
            <p className="text-muted-foreground text-xs">
              ຄິດຄ່າສອນຕໍ່ຄັ້ງ · ບໍ່ແຍກຕາມສະຖານທີ່
            </p>
            <PayoutBasisField
              name="roomRentalAssistPayoutBasis"
              label="ວິທີຄິດ"
            />
            <FormPrice
              name="roomRentalAssistAmountKip"
              label="ຈຳນວນ (₭)"
              placeholder="0"
              currencySymbol="₭"
            />
          </div>

          <div className="space-y-3 rounded-md border p-4">
            <p className="font-medium text-sm">
              ຊ່ວຍສອນຄລາດສ໌ (TA)
            </p>
            <p className="text-muted-foreground text-xs">
              ຄິດຄ່າຈ້າງຕໍ່ຄັ້ງ · ແຍກຕາມສະຖານທີ່
            </p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-1 rounded-md border bg-muted/30 p-3">
                <p className="font-medium text-muted-foreground text-xs">
                  ສະຕູດິໂອ
                </p>
                <LocationPayoutFields
                  basisName="generalClassAssistStudioPayoutBasis"
                  amountName="generalClassAssistStudioAmountKip"
                  basisLabel="ວິທີຄິດ"
                />
              </div>
              <div className="space-y-1 rounded-md border bg-muted/30 p-3">
                <p className="font-medium text-muted-foreground text-xs">
                  ນອກສະຖານທີ່
                </p>
                <LocationPayoutFields
                  basisName="generalClassAssistOffSitePayoutBasis"
                  amountName="generalClassAssistOffSiteAmountKip"
                  basisLabel="ວິທີຄິດ"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-xl border bg-card p-5 shadow-sm">
          <FormTextarea
            name="notes"
            label="ໝາຍເຫດ"
            placeholder="ປ້ອນໝາຍເຫດ (ບໍ່ບັງຄັບ)"
            rows={3}
          />

          <RHF.Controller
            name="status"
            control={methods.control}
            render={({ field }) => (
              <div className="mt-4 space-y-2">
                <label htmlFor="status" className="font-medium text-sm">
                  ສະຖານະ
                </label>
                <div className="inline-flex rounded-lg border bg-muted/30 p-1">
                  <Button
                    type="button"
                    size="sm"
                    variant={field.value === "active" ? "default" : "ghost"}
                    className={cn(
                      "min-w-[88px] rounded-md",
                      field.value === "active" && "shadow-sm",
                    )}
                    onClick={() => field.onChange("active")}
                  >
                    ໃຊ້ງານ
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={field.value === "inactive" ? "default" : "ghost"}
                    className={cn(
                      "min-w-[88px] rounded-md",
                      field.value === "inactive" && "shadow-sm",
                    )}
                    onClick={() => field.onChange("inactive")}
                  >
                    ບໍ່ໃຊ້ງານ
                  </Button>
                </div>
              </div>
            )}
          />
        </section>
      </div>

      <div className="flex justify-end gap-2 border-t pt-4">
        {isDevMode && (
          <Button
            type="button"
            variant="outline"
            onClick={handleAutoFill}
            disabled={submitting}
          >
            <SparklesIcon className="size-4" />
            Auto Fill
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={() => methods.reset()}
          disabled={submitting}
        >
          ລ້າງ
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
        </Button>
      </div>
    </FormRoot>
  );
}
